import { Injectable } from '@angular/core'
import {
  NgModalServiceInterface,
  ModalResponse,
} from '../../models/modal.model'
import { NgNotificationServiceInterface } from '../../models/notification.model'
import {
  ProductModalCode,
  ProductNotificationCode,
  parseProductModal,
  parseProductNotification,
} from './libs/product-notification.lib'
import {
  Product,
  ProductField,
  ProductSearchParams,
  ProductsListingPage,
} from './product.model'
import { ProductsListingData } from './models/products-listing.model'
import {
  combineLatest,
  concatMap,
  from,
  last,
  map,
  Observable,
  of,
  scan,
  switchMap,
} from 'rxjs'
import { ProductsService } from './products.service'
import { Page } from '../../models/util.model'
import { Supplier, SuppliersService } from '../suppliers'
import { parseProductPageKeys } from './libs/products-page.lib'

@Injectable({
  providedIn: 'root',
})
export class ProductsRepository {
  constructor(
    private productsService: ProductsService,
    private suppliersService: SuppliersService,
    private ngModalService?: NgModalServiceInterface,
    private ngNotificationService?: NgNotificationServiceInterface,
  ) {}

  /**
   * Search products and external data
   * @param searchParams - the search params
   * @param fields - the fields
   * @param listingData - the already loaded listing data
   * @returns the observable for search products
   */
  searchProducts$(
    searchParams: ProductSearchParams,
    fields: ProductField[],
    listingData?: ProductsListingData,
    returnAll?: boolean,
  ): Observable<ProductsListingPage> {
    const needsPagination =
      searchParams.limit !== undefined &&
      searchParams.limit > this.productsService.limit

    return needsPagination
      ? this._paginateProducts$(searchParams, fields, listingData, returnAll)
      : this._searchProducts$(searchParams, fields, listingData, returnAll)
  }

  /**
   * Search goods returns with relative external data
   * @param searchParams - the search params
   * @param fields - the products fields
   * @param listingData - the already loaded listing data
   * @returns the observable<ProductsListingPage> for search products
   */
  private _searchProducts$(
    searchParams: ProductSearchParams,
    fields: ProductField[],
    listingData?: ProductsListingData,
    returnAll?: boolean,
  ): Observable<ProductsListingPage> {
    return this.productsService
      .search$(searchParams, returnAll)
      .pipe(
        switchMap((page) =>
          this._loadExtData$(page, fields, listingData).pipe(
            map((extData) => ({ ...page, extData })),
          ),
        ),
      )
  }

  /**
   * Load external page products data
   * @param page - the products page
   * @param fields - the products fields
   * @returns the observable for load external data
   */
  private _loadExtData$(
    page: Page<Product>,
    fields: ProductField[],
    listingData?: ProductsListingData,
  ): Observable<ProductsListingData> {
    const extDataKeys = parseProductPageKeys(page)
    const obs$: Partial<{
      suppliers: Observable<Supplier[]>
    }> = {}

    // Only load channels if we don't already have them or if we need new ones
    if (fields.includes('suppliers._id') && extDataKeys.suppliersIds) {
      const existingSuppliers = listingData?.suppliers || []
      const hasAllSuppliers = extDataKeys.suppliersIds.every((id) =>
        existingSuppliers.some((supplier) => supplier._id === id),
      )

      if (!hasAllSuppliers) {
        obs$.suppliers = this.suppliersService.store$(
          extDataKeys.suppliersIds,
          existingSuppliers,
        )
      } else {
        obs$.suppliers = of(existingSuppliers)
      }
    }

    if (!Object.keys(obs$).length) {
      return of({} as ProductsListingData)
    }

    return combineLatest(obs$).pipe(
      map((results) => {
        const extData: ProductsListingData = {}
        if (results.suppliers) extData.suppliers = results.suppliers
        return extData
      }),
    )
  }

  /**
   * Search more than 200 products with relative external data
   * @param searchParams - the search params
   * @param fields - the products fields
   * @param listingData - the already loaded listing data
   * @returns the observable<ProductsListingPage> for search products
   */
  private _paginateProducts$(
    searchParams: ProductSearchParams,
    fields: ProductField[],
    listingData?: ProductsListingData,
    returnAll?: boolean,
  ): Observable<ProductsListingPage> {
    const totalCount = searchParams.limit || this.productsService.limit
    const pagesCount = Math.ceil(totalCount / this.productsService.limit)

    return from(
      Array.from({ length: pagesCount }, (_, i) => ({
        ...searchParams,
        limit: this.productsService.limit,
        offset: i * this.productsService.limit,
      })),
    ).pipe(
      concatMap((pageParams) =>
        this._searchProducts$(pageParams, fields, listingData, returnAll),
      ),
      scan<ProductsListingPage, ProductsListingPage>(
        (acc, current) => ({
          totalCount: (acc.totalCount || 0) + (current.totalCount || 0),
          data: [...(acc.data || []), ...(current.data || [])],
          extData: { ...acc.extData, ...current.extData },
        }),
        { totalCount: 0, data: [], extData: {} } as ProductsListingPage,
      ),
      last(),
    )
  }

  notify(options: { code?: ProductNotificationCode; message?: string }): void {
    this.ngNotificationService?.show(
      options.message
        ? { title: 'Error', message: options.message, color: 'error' }
        : parseProductNotification(options.code),
    )
  }

  async alert(
    code: ProductModalCode,
    product?: Product,
  ): Promise<ModalResponse> {
    const settings = parseProductModal(code, product)
    if (!this.ngModalService || !settings) {
      return 'closed'
    }
    return await this.ngModalService.showDialog(settings)
  }
}
